'use client'

import Link from 'next/link'

import { ArrowIcon } from '@/components/icons/ArrowIcon'
import { FeaturedCard } from '@/components/ui/featured-card'
import { LEARN_BASE_PATH } from '@/utilities/learnBasePath'
import type { Learn } from '@/payload-types'

interface Props {
  article: Learn
  variant?: 'light' | 'dark' | 'index'
}

export function LearnFeaturedCard({ article, variant = 'light' }: Props) {
  const articleHref = `${LEARN_BASE_PATH}/${article.slug}`
  const isDark = variant === 'dark'

  if (variant === 'index') {
    return (
      <Link
        href={articleHref}
        aria-label={`Read more: ${article.title}`}
        className="group flex flex-col justify-between gap-6 bg-black px-7 py-6 h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-lime focus-visible:ring-offset-2 focus-visible:ring-offset-black"
      >
        <div className="flex flex-col gap-3">
          <span className="body-small font-medium text-civo-lime leading-5">
            New Tutorial
          </span>
          <h3 className="subheading-large text-white line-clamp-2 transition-colors duration-300 group-hover:text-civo-lime">
            {article.title}
          </h3>
        </div>

        <div className="flex flex-col gap-1">
          <span className="inline-flex items-center gap-1 body-medium font-medium text-white transition-colors duration-300 group-hover:text-civo-lime">
            Read more
            <ArrowIcon className="shrink-0 size-3 transition-transform duration-300 group-hover:translate-x-1" stroke="currentColor" />
          </span>
          <div className="w-full h-px bg-white transition-colors duration-300 group-hover:bg-civo-lime" />
        </div>
      </Link>
    )
  }

  const publishedDate = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  return (
    <FeaturedCard
      title={article.title}
      href={articleHref}
      variant={isDark ? 'dark' : 'light'}
      topContent={publishedDate ? (
        <p
          className={`body-small mb-0 transition-colors duration-300 ${
            isDark ? 'text-white/70' : 'text-black/70 group-hover:text-white/70'
          }`}
        >
          {publishedDate}
        </p>
      ) : undefined}
    />
  )
}
